import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Loader2, Mail, BadgePoundSterling, ShieldCheck, Copy, Trash2, Check, Download, FileText, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (s) => { try { return new Date(s).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return s; } };
const gbp = (n) => `£${Number(n || 0).toFixed(2)}`;
const monthLabel = (ym) => { try { const [y, m] = ym.split('-'); return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }); } catch { return ym; } };
const EmptyRow = ({ msg }) => <p className="text-sm text-gray-400 py-8 text-center">{msg}</p>;

const Ops = ({ embedded = false }) => {
  const [emails, setEmails] = useState([]);
  const [unlocks, setUnlocks] = useState([]);
  const [assurance, setAssurance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromMonth, setFromMonth] = useState('');
  const [toMonth, setToMonth] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, u, a] = await Promise.all([
        axios.get(`${API}/admin/directory/email-queue`),
        axios.get(`${API}/admin/directory/product-unlocks`),
        axios.get(`${API}/admin/directory/assurance-requests`),
      ]);
      setEmails(e.data.items || []);
      setUnlocks(u.data.items || []);
      setAssurance(a.data.items || []);
    } catch { toast.error('Could not load ops data'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const markSent = async (em) => {
    try { await axios.post(`${API}/admin/directory/email-queue/${em.id}/mark-sent?source=${em.source}`); toast.success('Marked as sent'); load(); }
    catch { toast.error('Failed'); }
  };
  const sendOne = async (em) => {
    try {
      const res = await axios.post(`${API}/admin/directory/email-queue/${em.id}/send?source=${em.source}`);
      if (res.data?.ok) { toast.success('Email sent'); load(); }
      else toast.error(res.data?.message || 'Could not send');
    } catch { toast.error('Send failed'); }
  };
  const sendAllWelcome = async () => {
    try {
      const res = await axios.post(`${API}/admin/directory/email-queue/send-all?kind=supplier_welcome`);
      if (res.data?.ok && res.data?.sent > 0) { toast.success(`Sent ${res.data.sent} welcome email(s)`); load(); }
      else if (res.data?.sent > 0) { toast.success(`Sent ${res.data.sent}, ${res.data.failed} failed`); load(); }
      else toast.error(res.data?.message || 'Nothing sent');
    } catch { toast.error('Send failed'); }
  };
  const del = async (em) => {
    try { await axios.delete(`${API}/admin/directory/email-queue/${em.id}?source=${em.source}`); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };
  const copyHtml = async (em) => {
    try { await navigator.clipboard.writeText(`To: ${em.to}\nSubject: ${em.subject}\n\n${em.html || ''}`); toast.success('Email copied to clipboard'); }
    catch { toast.error('Copy failed'); }
  };
  const setAssuranceStatus = async (req, status) => {
    try { await axios.post(`${API}/admin/directory/assurance-requests/${req.id}/status?status=${status}`); toast.success('Updated'); load(); }
    catch { toast.error('Failed'); }
  };
  const downloadReceipt = async (u) => {
    try {
      const res = await axios.get(`${API}/supplier-listing/product-unlock/receipt/${u.session_id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = `receipt-${u.session_id.slice(0, 12)}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { toast.error('No receipt available'); }
  };
  const inRange = useCallback((u) => {
    const ym = (u.created_at || '').slice(0, 7);
    if (fromMonth && ym < fromMonth) return false;
    if (toMonth && ym > toMonth) return false;
    return true;
  }, [fromMonth, toMonth]);

  const filteredUnlocks = useMemo(() => unlocks.filter(inRange), [unlocks, inRange]);

  const exportUnlocks = () => {
    const rows = [['Date', 'Invoice', 'Supplier', 'Product', 'Net (GBP)', 'VAT (GBP)', 'Total (GBP)', 'Status', 'Stripe session']];
    filteredUnlocks.forEach((u) => rows.push([u.created_at || '', u.invoice_number || '', u.supplier_name || '', u.product_name || '', Number(u.amount_net || 0).toFixed(2), Number(u.amount_vat || 0).toFixed(2), Number(u.amount || 0).toFixed(2), u.payment_status || '', u.session_id || '']));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `product-unlocks-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  // Payout reconciliation: paid unlocks (within the selected month range) grouped by month, most recent first, with a running cumulative count.
  const monthly = useMemo(() => {
    const paid = filteredUnlocks.filter((u) => u.payment_status === 'paid');
    const map = {};
    paid.forEach((u) => {
      const ym = (u.created_at || '').slice(0, 7) || 'unknown';
      if (!map[ym]) map[ym] = { ym, count: 0, net: 0, vat: 0, gross: 0 };
      map[ym].count += 1;
      map[ym].net += Number(u.amount_net || 0);
      map[ym].vat += Number(u.amount_vat || 0);
      map[ym].gross += Number(u.amount || 0);
    });
    const rows = Object.values(map).sort((a, b) => (a.ym < b.ym ? 1 : -1)); // desc
    // running count is cumulative from earliest → latest, displayed on each (desc) row
    let cum = paid.length;
    const withRunning = rows.map((r) => { const running = cum; cum -= r.count; return { ...r, running }; });
    const totals = paid.reduce((t, u) => ({ count: t.count + 1, net: t.net + Number(u.amount_net || 0), vat: t.vat + Number(u.amount_vat || 0), gross: t.gross + Number(u.amount || 0) }), { count: 0, net: 0, vat: 0, gross: 0 });
    return { rows: withRunning, totals };
  }, [filteredUnlocks]);

  const badge = (s) => s === 'sent' ? 'bg-emerald-100 text-emerald-700' : s === 'queued' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600';

  return (
    <div className={embedded ? '' : 'p-6 max-w-6xl'} data-testid="ops-page">
      {!embedded && <h1 className="font-heading text-2xl font-extrabold text-gray-900">Operations</h1>}
      <p className={`text-sm text-gray-500 ${embedded ? 'mb-3' : 'mb-5'}`}>Queued emails, extra-product purchases and assurance requests.</p>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div> : (
        <Tabs defaultValue="emails">
          <TabsList data-testid="ops-tabs">
            <TabsTrigger value="emails" data-testid="ops-tab-emails"><Mail className="w-4 h-4 mr-1.5" /> Email queue ({emails.length})</TabsTrigger>
            <TabsTrigger value="unlocks" data-testid="ops-tab-unlocks"><BadgePoundSterling className="w-4 h-4 mr-1.5" /> Product unlocks ({unlocks.length})</TabsTrigger>
            <TabsTrigger value="assurance" data-testid="ops-tab-assurance"><ShieldCheck className="w-4 h-4 mr-1.5" /> Assurance requests ({assurance.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="emails" className="mt-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <p className="text-xs text-gray-400">Emails are queued (live sending activates once Resend is verified + INVITE_EMAIL_ENABLED is on). Copy to send manually, send now, mark sent, or delete.</p>
              <button onClick={sendAllWelcome} className="text-xs font-semibold px-3 py-1.5 rounded-md bg-[#00A79D] text-white hover:bg-[#00857D] inline-flex items-center gap-1.5" data-testid="ops-send-welcome-all"><Send className="w-3.5 h-3.5" /> Send welcome emails</button>
            </div>
            {emails.length === 0 ? <EmptyRow msg="No emails queued." /> : (
              <div className="space-y-2">
                {emails.map((em) => (
                  <Card key={`${em.source}-${em.id}`} data-testid={`ops-email-${em.id}`}><CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{em.subject}</p>
                      <p className="text-sm text-gray-500 truncate">{em.to} · <span className="text-gray-400">{em.kind}</span>{em.send_after ? ` · send after ${fmt(em.send_after)}` : ''}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${badge(em.status)}`}>{em.status}</span>
                      {em.html && <button onClick={() => copyHtml(em)} title="Copy" className="text-gray-400 hover:text-[#00857D]" data-testid={`ops-email-copy-${em.id}`}><Copy className="w-4 h-4" /></button>}
                      {em.source === 'outbox' && em.status !== 'sent' && <button onClick={() => sendOne(em)} title="Send now" className="text-gray-400 hover:text-[#00857D]" data-testid={`ops-email-send-${em.id}`}><Send className="w-4 h-4" /></button>}
                      {em.status !== 'sent' && <button onClick={() => markSent(em)} title="Mark sent" className="text-gray-400 hover:text-emerald-600" data-testid={`ops-email-mark-${em.id}`}><Check className="w-4 h-4" /></button>}
                      <button onClick={() => del(em)} title="Delete" className="text-gray-400 hover:text-red-500" data-testid={`ops-email-del-${em.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </CardContent></Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="unlocks" className="mt-4">
            {unlocks.length === 0 ? <EmptyRow msg="No extra-product purchases yet." /> : (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-gray-900">Payout reconciliation</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="text-[11px] text-gray-500 inline-flex items-center gap-1">From
                      <input type="month" value={fromMonth} onChange={(e) => setFromMonth(e.target.value)} className="border border-[#E5E7EB] rounded-md px-2 py-1 text-xs text-gray-700" data-testid="ops-payout-from" />
                    </label>
                    <label className="text-[11px] text-gray-500 inline-flex items-center gap-1">To
                      <input type="month" value={toMonth} onChange={(e) => setToMonth(e.target.value)} className="border border-[#E5E7EB] rounded-md px-2 py-1 text-xs text-gray-700" data-testid="ops-payout-to" />
                    </label>
                    {(fromMonth || toMonth) && <button onClick={() => { setFromMonth(''); setToMonth(''); }} className="text-[11px] font-semibold text-gray-500 hover:text-gray-800 px-1.5" data-testid="ops-payout-clear">Clear</button>}
                    <button onClick={exportUnlocks} className="text-xs font-semibold px-3 py-1.5 rounded-md border border-[#00A79D] text-[#00857D] hover:bg-[#00A79D]/5 inline-flex items-center gap-1.5" data-testid="ops-unlocks-export"><Download className="w-3.5 h-3.5" /> Export CSV</button>
                  </div>
                </div>
                {monthly.rows.length === 0 ? (
                  <p className="text-xs text-gray-400 pb-2" data-testid="ops-payout-empty">{(fromMonth || toMonth) ? 'No paid unlocks in the selected range.' : 'No paid unlocks yet to reconcile.'}</p>
                ) : (
                  <Card data-testid="ops-payout-reconciliation"><CardContent className="p-0">
                    <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-100">
                          <th className="text-left font-semibold px-4 py-2.5">Month</th>
                          <th className="text-right font-semibold px-3 py-2.5">Sales</th>
                          <th className="text-right font-semibold px-3 py-2.5">Running total</th>
                          <th className="text-right font-semibold px-3 py-2.5">Net</th>
                          <th className="text-right font-semibold px-3 py-2.5">VAT</th>
                          <th className="text-right font-semibold px-4 py-2.5">Gross</th>
                        </tr>
                      </thead>
                      <tbody>
                        {monthly.rows.map((r) => (
                          <tr key={r.ym} className="border-b border-gray-50 last:border-0" data-testid={`ops-payout-month-${r.ym}`}>
                            <td className="px-4 py-2.5 font-medium text-gray-900">{monthLabel(r.ym)}</td>
                            <td className="px-3 py-2.5 text-right text-gray-700">{r.count}</td>
                            <td className="px-3 py-2.5 text-right text-gray-500" data-testid={`ops-payout-running-${r.ym}`}>{r.running}</td>
                            <td className="px-3 py-2.5 text-right text-gray-700">{gbp(r.net)}</td>
                            <td className="px-3 py-2.5 text-right text-gray-700">{gbp(r.vat)}</td>
                            <td className="px-4 py-2.5 text-right font-semibold text-gray-900">{gbp(r.gross)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-[#1A3A5F]/20 bg-gray-50/60" data-testid="ops-payout-totals">
                          <td className="px-4 py-2.5 font-bold text-gray-900">All-time</td>
                          <td className="px-3 py-2.5 text-right font-bold text-gray-900">{monthly.totals.count}</td>
                          <td className="px-3 py-2.5"></td>
                          <td className="px-3 py-2.5 text-right font-bold text-gray-900">{gbp(monthly.totals.net)}</td>
                          <td className="px-3 py-2.5 text-right font-bold text-gray-900">{gbp(monthly.totals.vat)}</td>
                          <td className="px-4 py-2.5 text-right font-bold text-gray-900">{gbp(monthly.totals.gross)}</td>
                        </tr>
                      </tfoot>
                    </table>
                    </div>
                  </CardContent></Card>
                )}
                <p className="text-sm font-semibold text-gray-900 pt-2">All purchases{(fromMonth || toMonth) ? ` (${filteredUnlocks.length})` : ''}</p>
                {filteredUnlocks.length === 0 ? (
                  <p className="text-xs text-gray-400" data-testid="ops-unlocks-none-in-range">No purchases in the selected range.</p>
                ) : filteredUnlocks.map((u) => (
                  <Card key={u.session_id} data-testid={`ops-unlock-${u.session_id}`}><CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-gray-900">{u.product_name}</p>
                      <p className="text-sm text-gray-500">{u.supplier_name} · {fmt(u.created_at)}{u.invoice_number ? ` · ${u.invoice_number}` : ''}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-right">
                        <span className="font-semibold text-gray-900 block">{gbp(u.amount)}</span>
                        <span className="text-[11px] text-gray-400 block">{gbp(u.amount_net)} + {gbp(u.amount_vat)} VAT</span>
                      </span>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${u.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>{u.payment_status || 'pending'}</span>
                      {u.payment_status === 'paid' && <button onClick={() => downloadReceipt(u)} className="text-xs font-semibold text-[#0077b6] hover:underline inline-flex items-center gap-1" data-testid={`ops-unlock-receipt-${u.session_id}`}><FileText className="w-3.5 h-3.5" /> Receipt</button>}
                    </div>
                  </CardContent></Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="assurance" className="mt-4">
            {assurance.length === 0 ? <EmptyRow msg="No assurance requests yet." /> : (
              <div className="space-y-2">
                {assurance.map((r) => (
                  <Card key={r.id} data-testid={`ops-assurance-${r.id}`}><CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-gray-900">{r.supplier_name} <span className={`ml-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${r.kind === 'ctc_review' ? 'bg-emerald-100 text-emerald-800' : 'bg-orange-100 text-orange-800'}`} data-testid={`ops-assurance-kind-${r.id}`}>{r.label || 'Care City assurance'}</span></p>
                      <p className="text-sm text-gray-500">{r.email} · {fmt(r.created_at)}{r.note ? ` · "${r.note}"` : ''}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${r.status === 'done' ? 'bg-emerald-100 text-emerald-700' : r.status === 'declined' ? 'bg-red-100 text-red-700' : r.status === 'in_review' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-800'}`}>{r.status}</span>
                      {r.status !== 'in_review' && r.status !== 'done' && <button onClick={() => setAssuranceStatus(r, 'in_review')} className="text-xs font-semibold text-blue-700 hover:underline" data-testid={`ops-assurance-review-${r.id}`}>Start review</button>}
                      {r.register_ref ? <Link to="/admin/review-register" className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 hover:underline" data-testid={`ops-assurance-on-register-${r.id}`}>On register · {r.register_ref}</Link>
                        : ['in_review', 'done'].includes(r.status) && <Link to={`/admin/review-register?from_request=${r.id}`} className="text-xs font-semibold text-[#00857D] hover:underline inline-flex items-center gap-1" data-testid={`ops-assurance-add-register-${r.id}`}><ShieldCheck className="w-3.5 h-3.5" /> Add to register</Link>}
                      {r.status !== 'done' && <button onClick={() => setAssuranceStatus(r, 'done')} className="text-xs font-semibold text-emerald-700 hover:underline" data-testid={`ops-assurance-done-${r.id}`}>Mark done</button>}
                    </div>
                  </CardContent></Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default Ops;
